import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
  TextField, Button, Checkbox, FormControlLabel, Table, TableBody, TableCell,
  TableHead, TableRow, Box, CircularProgress, Alert, Paper, Chip, SxProps, Theme
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useLocation, useNavigate } from 'react-router-dom';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import AccountPickerMulti from '../components/AccountPickerMulti';
import AccountPickerSingle from '../components/AccountPickerSingle';
import type { AccountGroup } from '../components/AccountPickerMulti';

interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface WordSet {
  id: string;
  set_name: string;
}

interface Post {
  id: number;
  text: string | null;
  date: string;
  media_urls: string[];
}

interface JobStatus {
  task_id: string;
  state: 'PENDING' | 'SUCCESS' | 'FAILURE';
  result?: { file_url: string; filename: string };
  error?: string;
}

type Tab = 'channel-message' | 'channel-messages' | 'small-group-members' | 'large-group-members' | 'find-channels';

const tabInfo: Record<Tab, {
  title: string;
  icon: React.ReactNode;
  howTitle: string;
  chips: string[];
  description: string;
  details: {
    what: string;
    when: string[];
    result: string;
    features: string[];
  }
  resultNote?: string;
}> = {
  'channel-message': {
    title: 'Сообщения канала (быстрый)',
    howTitle: 'быстрый парсинг сообщений канала',
    icon: <SpeedIcon sx={{ fontSize: 40, color: '#1E40AF' }} />,
    chips: ['Мгновенно', 'До 100 постов', 'Фильтрация по стоп - и ключевым словам'],
    description: 'Идеально для быстрой проверки последних сообщений. Поддерживает стоп-слова, ключевые слова и загрузку медиа.',
    details: {
      what: 'Забирает последние N постов из канала.',
      when: ['Проверить активность', 'Найти нужный пост', 'Собрать медиа'],
      result: 'Таблица с текстом, датой и ссылками на скачивание медиа прямо в интерфейсе.',
      features: ['Фильтрация по стоп/ключевым словам', 'Настройка "Не повторять предыдущий пост" - после первого использования запоминает последний опубликованный пост и затем при включенном режиме возвращает только новые посты в канале', 'Очистка текста']
    },
    resultNote: 'Результат отображается сразу в интерфейсе.'
  },
  'channel-messages': {
    title: 'Сообщения канала (полный)',
    howTitle: 'Парсинг всех постов и публикационной активности канала',
    icon: <StorageIcon sx={{ fontSize: 40, color: '#1E40AF' }} />,
    chips: ['Сообщения и медиа', 'Доступ к базе комментаторов', 'Аналитика активности канала'],
    description: 'Полный сбор сообщений с комментаторами, анализом публикационной активности, комментариями и реакциями. Требует времени, сохраняется в выгрузках и приходит в бот',
    details: {
      what: 'Собирает ВСЁ даже из закрытого канала: посты, комментарии, реакции, медиа.',
      when: ['Анализ аудитории', 'Сбор контента', 'Подготовка к AI-обучению'],
      result: 'Файл: CSV / JSON / XLSX / Parquet',
      features: ['Лимиты по количеству и датам', 'Анализ реакций', 'Комментарии и данные комментаторов', 'Генерация аналитики (Доступно только при xlsx формате выгрузки)']
    }
  },
  'small-group-members': {
    title: 'Участники группы (до 10к)',
    howTitle: 'парсинг участников малых групп',
    icon: <GroupIcon sx={{ fontSize: 40, color: '#1E40AF' }} />,
    chips: ['Малые группы', 'Фильтрация участников', 'Сбор ЦА'],
    description: 'Быстрый парсинг участников небольших сообществ. Можно исключить ботов.',
    details: {
      what: 'Выгружает всех участников чата/группы до 10 000 человек.',
      when: ['Реклама', 'Приглашение в рассылку', 'Анализ целевой аудитории'],
      result: 'Файл с ID, именем, юзернеймом, статусом.',
      features: ['Получение контактов пользователей', 'Совместимость с автоматизациями Typhon', 'Мгновенно (до 1 минуты)']
    }
  },
  'large-group-members': {
    title: 'Участники группы (более 10к)',
    howTitle: 'парсинг участников крупных групп',
    icon: <GroupIcon sx={{ fontSize: 40, color: '#1E40AF' }} />,
    chips: ['Участники больших групп', 'Многоступенчатый сбор данных', "Сбор данных ЦА"],
    description: 'Глубокий парсинг крупных сообществ. Результаты сохраняются в выгрузке и присылаются в бот. Требует временных затрат.',
    details: {
      what: 'Выгружает участников из групп >10 000 человек.',
      when: ['Масштабные кампании через наши автоматизации', 'Сбор базы лидов', 'Социологические исследования'],
      result: 'Файл с участниками группы - телефоны, username, ФИО.',
      features: ['Обход лимитов', 'Фильтрация ботов', 'Высокий процент получения базы подписчиков']
    }
  },
  'find-channels': {
    title: 'Поиск каналов и групп',
    howTitle: 'поиск каналов и групп по ключевым словам',
    icon: <SearchIcon sx={{ fontSize: 40, color: '#1E40AF' }} />,
    chips: ['По ключевым словам', 'По хэштегам', 'По упоминаниям'],
    description: 'Автоматический поиск каналов и групп по заданным тематикам. Укажите набор слов и глубину — и получите список каналов с информацией о подписчиках и комментариях',
    details: {
      what: 'Ищет каналы по ключевым словам в названиях и описаниях.',
      when: ['Поиск каналов и групп для автоматизаций', 'Тематический обзор каналов по теме', 'Подготовка к рекламной кампании'],
      result: 'Файл: название, ссылка, количество подписчиков, описание.',
      features: ['Глубокий поиск по ссылкам и упоминаниям',  'Поиск по хэштегам']
    }
  }
};

const Parsing: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState<Tab>('channel-message');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [stopWordsSets, setStopWordsSets] = useState<WordSet[]>([]);
  const [includeWordsSets, setIncludeWordsSets] = useState<WordSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);

  // Табы, заблокированные для free-уровня
  const freeBlockedTabs: Tab[] = ['channel-messages', 'large-group-members'];

  const [tabState, setTabState] = useState<Record<Tab, any>>({
    'channel-message': {
      accountName: '',
      channel: '',
      amount: '1',
      timeInterval: '',
      media: false,
      latestNonRepeat: false,
      clearText: false,
      stopWords: '',
      includeWords: '',
      posts: []
    },
    'channel-messages': {
      accountName: '',
      channel: '',
      limit: '',
      minDate: '',
      maxDate:'',
      exportName: '',
      fileFormat: 'csv',
      media: false,
      clearText: false,
      reactionsAnalysis: false,
      withComments: false,
      withAnalysis: false,
      stopWords: '',
      includeWords: '',
      jobStatus: null,
      showAnalytics: false,
    },
    'small-group-members': {
      accountName: '',
      groupName: '',
      exportName: '',
      fileFormat: 'csv',
      withBots: false,
      jobStatus: null
    },
    'large-group-members': {
      accountNames: [] as string[],
      groupName: '',
      exportName: '',
      fileFormat: 'csv',
      withBots: false,
      jobStatus: null
    },
    'find-channels': {
      accountName: '',
      keywords: '',
      maxDepth: '1',
      exportName: '',
      fileFormat: 'csv',
      jobStatus: null
    },
  });

  const current = tabState[activeTab];
  const info = tabInfo[activeTab];

  useEffect(() => {
    const match = location.pathname.match(/\/parsing\/([a-z-]+)/);
    if (match && tabInfo[match[1] as Tab]) setActiveTab(match[1] as Tab);
  }, [location.pathname]);

  // Редирект если free-пользователь пытается открыть заблокированный таб напрямую
  useEffect(() => {
    if (!freeBlockedTabs.includes(activeTab)) return;

    const checkAndRedirect = async () => {
      try {
        const res = await api.get('/subscriptions/limits');
        if (res.data.user_level === 'free') {
          navigate('/parsing/channel-message', { replace: true });
        }
      } catch {
        // при ошибке не редиректим
      }
    };
    checkAndRedirect();
  }, [activeTab]);

  useEffect(() => {
    const fetch = async () => {
      if (!auth?.isAuthenticated) return;
      setIsLoading(true);
      try {
        const [a, s, i] = await Promise.all([
          api.get('/accounts'),
          api.get('/stopwords'),
          api.get('/includewords')
        ]);
        api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
        setAccounts(a.data.accounts || []);
        setStopWordsSets(s.data.sets || []);
        setIncludeWordsSets(i.data.sets || []);
      } catch {
        setMessage({ text: 'Ошибка загрузки данных', type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [auth?.isAuthenticated]);

  useEffect(() => {
    let i: NodeJS.Timeout | null = null;
    const job = current.jobStatus;
    if (job?.state === 'PENDING') {
      i = setInterval(async () => {
        try {
          const r = await api.get(`/parse/job_status/${job.task_id}`);
          const s: JobStatus = r.data;
          setTabState(p => ({ ...p, [activeTab]: { ...p[activeTab], jobStatus: s } }));
          if (s.state !== 'PENDING') {
            clearInterval(i!);
            setMessage({
              text: s.state === 'SUCCESS' ? `Готово: ${s.result?.filename}` : s.error || 'Ошибка',
              type: s.state === 'SUCCESS' ? 'success' : 'error'
            });
          }
        } catch {
          clearInterval(i!);
          setMessage({ text: 'Ошибка проверки статуса', type: 'error' });
        }
      }, 3000);
    }
    return () => {
      if (i) clearInterval(i);
    };
  }, [current.jobStatus, activeTab]);

  const update = (k: string, v: any) => setTabState(p => ({ ...p, [activeTab]: { ...p[activeTab], [k]: v } }));

  const startAsync = async (
      ep: string,
      p: any,
      config?: any
  ) => {
    if (
        !current.exportName ||
        (!current.accountName && !current.accountNames)
    ) {
      setMessage({text: 'Заполните обязательные поля', type: 'error'});
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const r = await api.get(ep, {
        params: p,
        ...config
      });

      update('jobStatus', {task_id: r.data.job_id, state: 'PENDING'});
      setMessage({text: 'Задача запущена...', type: 'info'});

    } catch {
      setMessage({text: 'Ошибка запуска', type: 'error'});
    } finally {
      setIsLoading(false);
    }
  };

  const gridItem: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%' };

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка данных...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === КРАТКОЕ + ЧИПЫ === */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {info.icon}
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              {info.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {info.chips.map((chip, i) => (
                <Chip key={i} label={chip} size="small"
                  sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
              ))}
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              {info.description}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* === ПОДРОБНОЕ ОПИСАНИЕ — КАК В НЕЙРОКОММЕНТИНГЕ === */}
      {activeTab === 'channel-messages' ? (
  <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
    <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
      Как работает полный парсинг канала?
    </Typography>

    <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
      Собирает ВСЁ из канала: посты, комментарии, реакции, медиа. Также вы получаете комментаторов и их активность, и можете получить аналитический отчёт.
    </Typography>

    {/* Три колонки: Когда / Результат / Особенности */}
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Когда использовать</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 0.5 }}>• Анализ аудитории</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 0.5 }}>• Сбор контента</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 0.5 }}>• Подготовка к AI-обучению</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>• Конкурентный анализ</Typography>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Результат</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>Файл: CSV / JSON / XLSX / Parquet</Typography>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Особенности</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 0.5 }}>• Лимит и даты</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 0.5 }}>• Комментарии и реакции</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 0.5 }}>• Скачивание медиа</Typography>
        <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>• Глубокая аналитика (только XLSX)</Typography>
      </Box>
    </Box>

    {/* ВСЯ АНАЛИТИКА — ВСЕГДА ВИДНА, БЕЗ КНОПОК */}
    <Box sx={{ p: 4, bgcolor: '#FFFFFF', borderRadius: 2, border: '1px solid #DBEAFE' }}>
      <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
        Что входит в аналитику (только XLSX)
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>Сводка канала</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Всего сообщений, просмотров, реакций, комментариев</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Средние показатели, индекс лояльности</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Самый обсуждаемый день и час</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>Топ-50 постов</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• По индексу популярности</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Формула: просмотры × 0.1 + реакции × 2 + комментарии × 5</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>Тепловая карта активности</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Посты и комментарии по дням недели и часам</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>Комментаторы</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Топ активных пользователей</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Первый и последний комментарий</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>Реакции и настроение</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Топ эмодзи</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Позитивные / негативные / нейтральные</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Соотношение позитива к негативу</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontWeight: 600, color: '#1E40AF', mb: 1 }}>Ссылки и упоминания</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Все внешние ссылки</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>• Все @упоминания</Typography>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, p: 3, bgcolor: '#EBF8FF', borderRadius: 2, textAlign: 'center' }}>
        <Typography sx={{ color: '#1E40AF', fontWeight: 600 }}>
          Глубокий анализ активности каналов
        </Typography>
      </Box>
    </Box>

    <Typography sx={{ color: '#4B5563', mt: 4, fontStyle: 'italic' }}>
      После запуска вы получите уведомление и ссылку на скачивание.
    </Typography>
  </Box>
      ) : (
          // ← ДЛЯ ВСЕХ ОСТАЛЬНЫХ ВКЛАДОК ОСТАЁТСЯ СТАРЫЙ БЛОК
          <Box sx={{bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6}}>
            <Typography variant="h6" sx={{color: '#1E3A8A', mb: 3, fontWeight: 600}}>
              Как работает {info.howTitle}?
            </Typography>
            <Typography sx={{color: '#4B5563', mb: 3, lineHeight: 1.7}}>
              {info.details.what}
            </Typography>
            <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 4, mb: 3}}>
              <Box sx={{flex: 1}}>
                <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Когда использовать</Typography>
                {info.details.when.map((item: string, i: number) => (
                    <Typography key={i} sx={{color: '#4B5563', fontSize: '0.9rem', mb: 0.5}}>• {item}</Typography>
                ))}
              </Box>
              <Box sx={{flex: 1}}>
                <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Результат</Typography>
                <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>{info.details.result}</Typography>
              </Box>
              <Box sx={{flex: 1}}>
                <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Особенности</Typography>
                {info.details.features.map((feat: string, i: number) => (
                    <Typography key={i} sx={{color: '#4B5563', fontSize: '0.9rem', mb: 0.5}}>• {feat}</Typography>
                ))}
              </Box>
            </Box>
            <Typography sx={{color: '#4B5563', mt: 3, fontStyle: 'italic'}}>
              {info.resultNote || 'После запуска вы получите уведомление и ссылку на скачивание.'}
            </Typography>
          </Box>
      )}

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {current.jobStatus?.state === 'PENDING' && (
        <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: '#E6F3FF', borderRadius: 2 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: '#1E40AF', fontWeight: 500 }}>Задача выполняется...</Typography>
        </Box>
      )}

      {current.jobStatus?.state === 'SUCCESS' && current.jobStatus.result && (
        <Alert severity="success" sx={{ mb: 3 }} action={
          <Button href={current.jobStatus.result.file_url} size="small" startIcon={<DownloadIcon />}>
            Скачать
          </Button>
        }>
          Файл готов: <strong>{current.jobStatus.result.filename}</strong>
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* === Быстрый парсинг === */}
        {activeTab === 'channel-message' && (
          <>
            <Grid size={12} sx={gridItem}>
              <AccountPickerSingle
                accounts={accounts}
                selected={current.accountName}
                onChange={v => update('accountName', v)}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Канал" value={current.channel}
                onChange={e => update('channel', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={6} sx={gridItem}>
              <TextField label="Количество" type="number" value={current.amount}
                onChange={e => update('amount', e.target.value)} fullWidth />
            </Grid>
            <Grid size={6} sx={gridItem}>
              <TextField label="Охват" placeholder="Количество дней, часов, или минут (примеры: 1d, 2h или 10m)" value={current.timeInterval}
                onChange={e => update('timeInterval', e.target.value)} fullWidth />
            </Grid>
            <Grid size={6} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="stop-words-label">Стоп-слова</InputLabel>
                <Select
                  labelId="stop-words-label"
                  label="Стоп-слова"
                  value={current.stopWords}
                  onChange={e => update('stopWords', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {stopWordsSets.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.set_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="include-words-label">Ключевые слова</InputLabel>
                <Select
                  labelId="include-words-label"
                  label="Ключевые слова"
                  value={current.includeWords}
                  onChange={e => update('includeWords', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {includeWordsSets.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.set_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.media}
                onChange={e => update('media', e.target.checked)} />} label="Скачать медиа-файлы" />
            </Grid>
            <Grid size={4} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.latestNonRepeat}
                onChange={e => update('latestNonRepeat', e.target.checked)} />} label="Не повторять последнее опубликованное" />
            </Grid>
            <Grid size={4} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.clearText}
                onChange={e => update('clearText', e.target.checked)} />} label="Очистить текст от служебных символов" />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!current.accountName || !current.channel) {
                    setMessage({ text: 'Заполните поля', type: 'error' });
                    return;
                  }
                  setIsLoading(true);
                  setMessage(null);
                  try {
                    const r = await api.get('/parse/ui/channel_message', {
                      params: {
                        account_name: current.accountName,
                        channel: current.channel,
                        amount: Number(current.amount) || 1,
                        time_interval: current.timeInterval || undefined,
                        media: current.media,
                        latest_non_repeat: current.latestNonRepeat,
                        clear_text: current.clearText,
                        stop_words: current.stopWords || undefined,
                        include_words: current.includeWords || undefined,
                      }
                    });
                    update('posts', r.data.posts || []);
                    setMessage({ text: 'Готово!', type: 'success' });
                  } catch {
                    setMessage({ text: 'Ошибка парсинга', type: 'error' });
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading}
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
              >
                Запустить
              </Button>
            </Grid>

            {current.posts?.length > 0 && (
              <Grid size={12} sx={{ mt: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Текст</TableCell>
                      <TableCell>Дата</TableCell>
                      <TableCell>Медиа</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {current.posts.map((p: Post) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.id}</TableCell>
                        <TableCell>{p.text || '—'}</TableCell>
                        <TableCell>{new Date(p.date).toLocaleString('ru')}</TableCell>
                        <TableCell>
                          {p.media_urls.map((url, i) => (
                            <Button key={i} size="small"
                              onClick={() => window.open(url, '_blank')} sx={{ mr: 1 }}>
                              Медиа {i + 1}
                            </Button>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          </>
        )}

        {/* === Полный парсинг === */}
        {activeTab === 'channel-messages' && (
          <>
            <Grid size={12} sx={gridItem}>
              <AccountPickerSingle
                accounts={accounts}
                selected={current.accountName}
                onChange={v => update('accountName', v)}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Канал" value={current.channel}
                onChange={e => update('channel', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={6} sx={gridItem}>
              <TextField label="Лимит" type="number" value={current.limit}
                onChange={e => update('limit', e.target.value)} fullWidth />
            </Grid>
            <Grid size={6} sx={gridItem}>
            <TextField
              label="C какой даты (вкл.)"
              type="date"
              value={current.minDate}
              onChange={e => update('minDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* НОВОЕ ПОЛЕ: Дата окончания */}
          <Grid size={6} sx={gridItem}>
            <TextField
              label="По какую дату (вкл.)"
              type="date"
              value={current.maxDate}
              onChange={e => update('maxDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Необязательно"
            />
          </Grid>
            <Grid size={6} sx={gridItem}>
              <TextField label="Имя выгрузки" value={current.exportName}
                onChange={e => update('exportName', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={6} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="file-format-label">Формат файла</InputLabel>
                <Select
                  labelId="file-format-label"
                  label="Формат файла"
                  value={current.fileFormat}
                  onChange={e => update('fileFormat', e.target.value)}
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="xlsx">XLSX</MenuItem>
                  <MenuItem value="parquet">Parquet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="stop-words-label">Стоп-слова</InputLabel>
                <Select
                  labelId="stop-words-label"
                  label="Стоп-слова"
                  value={current.stopWords}
                  onChange={e => update('stopWords', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {stopWordsSets.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.set_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="include-words-label">Ключевые слова</InputLabel>
                <Select
                  labelId="include-words-label"
                  label="Ключевые слова"
                  value={current.includeWords}
                  onChange={e => update('includeWords', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {includeWordsSets.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.set_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={3} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.media}
                onChange={e => update('media', e.target.checked)} />} label="Скачать медиа-файлы" />
            </Grid>
            <Grid size={3} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.clearText}
                onChange={e => update('clearText', e.target.checked)} />} label="Очистить текст от служебных символов" />
            </Grid>
            <Grid size={3} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.reactionsAnalysis}
                onChange={e => update('reactionsAnalysis', e.target.checked)} />} label="Собрать реакции и проанализировать их для постов" />
            </Grid>
            <Grid size={3} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.withComments}
                onChange={e => update('withComments', e.target.checked)} />} label="Комментарии" />
            </Grid>
            <Grid size={3} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.withAnalysis}
                onChange={e => update('withAnalysis', e.target.checked)} />} label="Аналитика" />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                variant="contained"
                onClick={() => startAsync('/parse/ui/channel_messages', {
                  account_name: current.accountName,
                  channel: current.channel,
                  limit: current.limit || undefined,
                  min_date: current.minDate || undefined,
                  max_date: current.maxDate || undefined,
                  export_name: current.exportName,
                  file_format: current.fileFormat,
                  media: current.media,
                  clear_text: current.clearText,
                  reaction_analisys: current.reactionsAnalysis,
                  with_comments: current.withComments,
                  with_analysys: current.withAnalysis,
                  stop_words: current.stopWords || undefined,
                  include_words: current.includeWords || undefined,
                })}
                disabled={isLoading || !current.accountName || !current.channel || !current.exportName}
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
              >
                Запустить
              </Button>
            </Grid>
          </>
        )}

        {/* === Малые группы === */}
        {activeTab === 'small-group-members' && (
          <>
            <Grid size={12} sx={gridItem}>
              <AccountPickerSingle
                accounts={accounts}
                selected={current.accountName}
                onChange={v => update('accountName', v)}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Имя группы" value={current.groupName}
                onChange={e => update('groupName', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Имя выгрузки" value={current.exportName}
                onChange={e => update('exportName', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="file-format-label">Формат файла</InputLabel>
                <Select
                  labelId="file-format-label"
                  label="Формат файла"
                  value={current.fileFormat}
                  onChange={e => update('fileFormat', e.target.value)}
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="xlsx">XLSX</MenuItem>
                  <MenuItem value="parquet">Parquet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.withBots}
                onChange={e => update('withBots', e.target.checked)} />} label="Включить ботов" />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                variant="contained"
                onClick={() => startAsync('/parse/ui/small/group_members', {
                  account_name: current.accountName,
                  group_name: current.groupName,
                  export_name: current.exportName,
                  file_format: current.fileFormat,
                  with_bots: current.withBots,
                })}
                disabled={isLoading || !current.accountName || !current.groupName || !current.exportName}
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
              >
                Запустить
              </Button>
            </Grid>
          </>
        )}

        {/* === Большие группы === */}
       {activeTab === 'large-group-members' && (
          <>
            <Grid size={12} sx={gridItem}>
              <AccountPickerMulti
                accounts={accounts}
                selected={current.accountNames}
                onChange={names => update('accountNames', names)}
                placeholder="Аккаунты (рекомендуется несколько)"
                groups={groups}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Имя группы" value={current.groupName}
                onChange={e => update('groupName', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Имя выгрузки" value={current.exportName}
                onChange={e => update('exportName', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="file-format-label">Формат файла</InputLabel>
                <Select
                  labelId="file-format-label"
                  label="Формат файла"
                  value={current.fileFormat}
                  onChange={e => update('fileFormat', e.target.value)}
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="xlsx">XLSX</MenuItem>
                  <MenuItem value="parquet">Parquet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12} sx={gridItem}>
              <FormControlLabel control={<Checkbox checked={current.withBots}
                onChange={e => update('withBots', e.target.checked)} />} label="Включить ботов" />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                  variant="contained"
                  onClick={() =>
                      startAsync(
                          '/parse/ui/large/group_members',
                          {
                            account_names: current.accountNames,
                            group_name: current.groupName,
                            export_name: current.exportName,
                            file_format: current.fileFormat,
                            with_bots: current.withBots,
                          },
                          {
                            paramsSerializer: (params: any) => {
                              const searchParams = new URLSearchParams();

                              Object.entries(params).forEach(([key, value]) => {
                                if (Array.isArray(value)) {
                                  value.forEach(v => searchParams.append(key, String(v)));
                                } else if (value !== undefined && value !== null) {
                                  searchParams.append(key, String(value));
                                }
                              });

                              return searchParams.toString();
                            }
                          }
                      )
                  }
                  disabled={
                      isLoading ||
                      !current.accountNames.length ||
                      !current.groupName ||
                      !current.exportName
                  }
                  sx={{bgcolor: '#1E3A8A', '&:hover': {bgcolor: '#1E40AF'}}}
              >
                Запустить
              </Button>
            </Grid>
          </>
        )}

        {/* === Поиск каналов === */}
        {activeTab === 'find-channels' && (
          <>
            <Grid size={12} sx={gridItem}>
              <AccountPickerSingle
                accounts={accounts}
                selected={current.accountName}
                onChange={v => update('accountName', v)}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Ключевые слова (через запятую)" value={current.keywords}
                onChange={e => update('keywords', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Глубина поиска" type="number" value={current.maxDepth}
                onChange={e => update('maxDepth', e.target.value)} fullWidth />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField label="Имя выгрузки" value={current.exportName}
                onChange={e => update('exportName', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <FormControl fullWidth>
                <InputLabel id="file-format-label">Формат файла</InputLabel>
                <Select
                  labelId="file-format-label"
                  label="Формат файла"
                  value={current.fileFormat}
                  onChange={e => update('fileFormat', e.target.value)}
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="xlsx">XLSX</MenuItem>
                  <MenuItem value="parquet">Parquet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                variant="contained"
                onClick={() => {
                  const kw = current.keywords.split(',').map((k: string) => k.trim()).filter(Boolean);
                  if (!kw.length) {
                    setMessage({ text: 'Введите хотя бы одно слово', type: 'error' });
                    return;
                  }
                  startAsync('/parse/ui/find_channels', {
                    account_name: current.accountName,
                    keywords: kw.join(','),
                    max_depth: Number(current.maxDepth) || 1,
                    export_name: current.exportName,
                    file_format: current.fileFormat,
                  });
                }}
                disabled={isLoading || !current.accountName || !current.keywords || !current.exportName}
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
              >
                Запустить
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
};

export default Parsing;